"use client";

import Link from "next/link";
import { ArrowRight, Check, Wrench, Cog, Fingerprint } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { overview as data } from "@/lib/data/about";

const pillarIconMap = { Wrench, Cog, Fingerprint } as const;
const pillarAccentMap = {
  "What We Do": "var(--color-crimson)",
  "How We Work": "var(--color-purple)",
  "What Makes Us Different": "var(--color-cyan)",
} as const;

export function Overview() {
  return (
    <Section id="overview" className="relative">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-0 right-1/3 h-80 w-80 rounded-full bg-[var(--color-purple)] opacity-20 blur-[120px]" />
      </div>

      <Container>
        <Reveal>
          <div className="max-w-4xl">
            <span className="eyebrow-tiny text-[var(--color-purple)]">{data.eyebrow}</span>
            <h2 className="mt-3">{data.title}</h2>
          </div>
        </Reveal>

        <div className="mt-12 grid lg:grid-cols-[1.4fr_1fr] gap-10">
          <div className="space-y-5 max-w-3xl">
            {data.paragraphs.map((p, i) => (
              <Reveal key={i} delay={i * 60}>
                <p className="text-body leading-relaxed text-[rgb(var(--fg-muted))]">
                  {p}
                </p>
              </Reveal>
            ))}

            <Reveal delay={300}>
              <div className="pt-4 flex flex-wrap items-center gap-3">
                {data.cta.map((c, i) => (
                  <Link
                    key={c.label}
                    href={c.href}
                    className={
                      i === 0
                        ? "btn-primary-gradient h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"
                        : "btn-glass h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"
                    }
                  >
                    {c.label} <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                ))}
              </div>
            </Reveal>
          </div>

          <Reveal delay={120}>
            <div className="glass rounded-2xl p-6 space-y-5">
              <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))]">The Three Pillars</div>
              {data.pillars.map((pillar) => {
                const Icon = pillarIconMap[pillar.label as keyof typeof pillarIconMap] ?? Cog;
                const accent = pillarAccentMap[pillar.label as keyof typeof pillarAccentMap] ?? "var(--color-crimson)";
                return (
                  <div key={pillar.label} className="flex items-start gap-3">
                    <span
                      className="grid place-items-center h-9 w-9 rounded-lg shrink-0"
                      style={{
                        background: `color-mix(in oklch, ${accent} 18%, transparent)`,
                        boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 35%, transparent)`,
                      }}
                    >
                      <Icon className="h-4 w-4" style={{ color: accent }} />
                    </span>
                    <div>
                      <div className="font-semibold text-sm">{pillar.label}</div>
                      <div className="text-xs text-[rgb(var(--fg-muted))] mt-0.5 leading-relaxed">{pillar.body}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Reveal>
        </div>
      </Container>
    </Section>
  );
}
